// Copy of components/transactionSearchPage/TransactionSearchPage.epic.js on its own Redux slice for the Payments - Beta tab.
import { combineEpics, ofType } from "redux-observable";
import { v4 as uuidv4 } from "uuid";
import * as RxOp from "rxjs/operators";
import * as Rx from "rxjs";
import S from "sanctuary";
import moment from "moment-timezone";
import { transactionSearch, translateTransactionSearch } from "@/APIClient";
import { requestEpic } from "@/APIClient/observable.util";
import {
  changeCalendarDates,
  closeDatesModal,
} from "@/components/datePickerV2Beta/DatePickerV2Beta.state";
import { CLOSE_DATES_MODAL as CLASSIC_CLOSE_DATES_MODAL } from "@/components/datePicker/DatePicker.state";
import {
  getUserTimezone,
  getCurrentUserClientBrand,
  getCurrentUserAgencyNames,
} from "@/components/currentUser/CurrentUser.state";
import {
  getTransactionSearchStartDate,
  getTransactionSearchEndDate,
  isDatesOpen,
} from "@/components/datePickerV2Beta/DatePickerV2Beta.selectors";
import {
  createAlert,
  clearAllAlerts,
  ALERT_LEVELS,
} from "@/components/alerts/Alerts.state";
import {
  clearState,
  setTransactionSearchResults,
  getActiveSearchId,
  RESULTS_PER_PAGE,
} from "@/components/transactionSearchResultsV2Beta/TransactionSearchResultsV2Beta.state";
import { getAuthToken } from "@/util/session";

import {
  TRANSACTION_SEARCH_PENDING,
  SEARCH_REQUEST_FAILED,
  SEARCH_REQUEST_SUCCEEDED,
  REQUEST_SEARCH,
  REQUEST_PAGINATED_SEARCH,
  TRANSLATE_REQUEST,
  RESET_STATE,
  transactionSearchPending,
  getTransactionDateFilter,
  searchRequestSucceeded,
  searchRequestFailed,
  translateSucceeded,
  translateFailed,
} from "./TransactionSearchPageV2Beta.state";
import {
  nextPendingTextFrame,
  closeModal,
} from "./TransactionSearchPageV2Beta.state";

import {
  AGENCIES_FILTER,
  isMultiValueFilter,
} from "@/components/transactionSearchPage/FiltersForm";
import {
  getAmountPredicate,
  amountMatchesPredicate,
} from "@/components/transactionSearchPage/amountFilter";
import {
  fetchAllForAmountPredicate,
  transactionAmount,
  resolveSearchDateDefaults,
  toTranslateError,
} from "@/components/transactionSearchPage/TransactionSearchPage.epic";

// Must match the ceiling fetchAllForAmountPredicate enforces in the classic epic.
const AMOUNT_FILTER_MAX_ROWS = 5000;

const updateSearchPendingTextEpic = (action$) =>
  action$.pipe(
    ofType(TRANSACTION_SEARCH_PENDING),
    RxOp.mergeMap(() =>
      Rx.timer(800, 800)
        .pipe(RxOp.mapTo(nextPendingTextFrame()))
        .pipe(
          RxOp.takeUntil(
            action$.pipe(
              ofType(SEARCH_REQUEST_FAILED, SEARCH_REQUEST_SUCCEEDED, RESET_STATE)
            )
          )
        )
    )
  );

const searchCriteria = (state) => {
  const criteriaForm = state.transactionSearchV2Beta.forms.criteriaForm;
  return Object.keys(criteriaForm).reduce((acc, key) => {
    let value = criteriaForm[key].rawValue;
    if (value === "") {
      return acc;
    } else {
      return { ...acc, [key]: value };
    }
  }, {});
};

const searchFilters = (state) => {
  const filtersForm = state.transactionSearchV2Beta.forms.filtersForm;
  return Object.keys(filtersForm).reduce((acc, key) => {
    const value = filtersForm[key].rawValue;
    if (value === "") {
      if (key === AGENCIES_FILTER) {
        return { ...acc, [key]: getCurrentUserAgencyNames(state) };
      }
      return acc;
    }
    // /payments expects each multi-select value as its own `param[]=` entry, so
    // split the comma-separated rawValue into an array for qs.stringify.
    if (isMultiValueFilter(key)) {
      return {
        ...acc,
        [key]: value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      };
    }
    return { ...acc, [key]: value };
  }, {});
};

const searchDates = (state, overrideDates = null) => {
  const dateFilter = getTransactionDateFilter(state);
  const timezone = getUserTimezone(state).value;

  // When the epic resolves an override, send exactly those bounds (either may
  // be null to omit it); do not mirror one bound onto the other.
  if (overrideDates) {
    const { startDate, endDate } = overrideDates;
    if (!startDate && !endDate) return {};
    return {
      ...(startDate
        ? {
            startDate: moment(startDate).startOf("day").tz(timezone).toISOString(),
          }
        : {}),
      ...(endDate
        ? { endDate: moment(endDate).endOf("day").tz(timezone).toISOString() }
        : {}),
      dateFilter,
    };
  }

  let startDate = getTransactionSearchStartDate(state);
  let endDate = getTransactionSearchEndDate(state);
  startDate = startDate || endDate;
  endDate = endDate || startDate;
  return startDate && endDate
    ? {
        startDate: moment(startDate).startOf("day").tz(timezone).toISOString(),
        endDate: moment(endDate).endOf("day").tz(timezone).toISOString(),
        dateFilter,
      }
    : {};
};

const getAmountPredicateFromState = (state) =>
  getAmountPredicate(
    state.transactionSearchV2Beta.forms.criteriaForm.amount?.rawValue
  );

const userInfo = (state) => ({
  token: S.fromMaybe("")(getAuthToken(state)),
  client: getCurrentUserClientBrand(state),
});

const searchParams = (state, page, overrideDates = null) => ({
  ...userInfo(state),
  page,
  resultsPerPage: RESULTS_PER_PAGE,
  ...searchDates(state, overrideDates),
  ...searchFilters(state),
  ...searchCriteria(state),
});

const performTransactionSearchEpic = (action$, state$) =>
  action$.pipe(
    ofType(REQUEST_SEARCH, REQUEST_PAGINATED_SEARCH),
    RxOp.mergeMap(({ payload = {} }) => {
      const searchId = payload.searchId ?? uuidv4();
      // Captured so the response is filtered by the predicate it was requested
      // with, not whatever the pill holds when it settles.
      const amountPredicate = getAmountPredicateFromState(state$.value);
      // True once the page is waiting on a newer search; a superseded search must
      // not settle or raise notices.
      const isSuperseded = () => {
        const activeSearchId = getActiveSearchId(state$.value);
        return Boolean(activeSearchId) && activeSearchId !== searchId;
      };
      const rawStartDate = getTransactionSearchStartDate(state$.value);
      const rawEndDate = getTransactionSearchEndDate(state$.value);
      const overrideDates = resolveSearchDateDefaults(rawStartDate, rawEndDate);
      const filledDefaultStart = Boolean(overrideDates && overrideDates.startDate);
      const defaultDateActions = filledDefaultStart
        ? [changeCalendarDates(overrideDates, false, true)]
        : [];
      const optimizedRangeAlert = filledDefaultStart
        ? [
            createAlert({
              type: ALERT_LEVELS.INFO,
              title: "Date Range Optimized",
              text: "Your search was too broad. We applied a 1-year date range to prevent the system from timing out. If you would like a different date range please adjust the dates.",
              ttl: S.Nothing,
            }),
          ]
        : [];
      return Rx.concat([
        Rx.of(
          // Clear the previous search's alerts first; emitted before
          // `optimizedRangeAlert` so this search's notice survives.
          clearAllAlerts(),
          ...defaultDateActions,
          ...optimizedRangeAlert,
          clearState({ searchId }),
          closeModal(),
          closeDatesModal(),
          transactionSearchPending()
        ),
        requestEpic({
          requestMethod: amountPredicate
            ? fetchAllForAmountPredicate
            : transactionSearch,
          requestArgs: searchParams(state$.value, payload.page, overrideDates),
          onSuccess: (result) => {
            if (isSuperseded()) return Rx.EMPTY;
            const rawTransactions = result.value.transactions;
            const serverTotal = S.fromMaybe(rawTransactions.length)(
              result.value.total
            );
            // /payments can't filter by amount range, so drop non-matching rows here;
            // the count matches what the user sees.
            const transactions = amountPredicate
              ? rawTransactions.filter((txn) =>
                  amountMatchesPredicate(transactionAmount(txn), amountPredicate)
                )
              : rawTransactions;
            const resultsCount = amountPredicate ? transactions.length : serverTotal;
            // Matches past AMOUNT_FILTER_MAX_ROWS are invisible; say so.
            const windowTruncated =
              Boolean(amountPredicate) && Boolean(result.value.truncated);
            return Rx.of(
              searchRequestSucceeded({ searchId }),
              setTransactionSearchResults({
                transactions,
                resultsCount,
                page: amountPredicate ? 1 : payload.page || 1,
                searchId,
                clientPaginated: Boolean(amountPredicate),
              }),
              ...(windowTruncated
                ? [
                    createAlert({
                      type: ALERT_LEVELS.INFO,
                      title: `Showing the First ${AMOUNT_FILTER_MAX_ROWS} Transactions`,
                      text: `Amount ranges are matched in your browser, against the first ${AMOUNT_FILTER_MAX_ROWS} transactions your other criteria returned. Matches beyond those are not shown. Narrow your date range or filters to be sure you are seeing every match.`,
                      ttl: S.Nothing,
                    }),
                  ]
                : [])
            );
          },
          onFailure: (error) =>
            isSuperseded()
              ? Rx.EMPTY
              : Rx.of(searchRequestFailed({ error: error.value, searchId })),
        }).pipe(
          // A newer search supersedes this one: stop paging and drop whatever
          // this search would have settled with.
          RxOp.takeUntil(
            action$.pipe(
              ofType(REQUEST_SEARCH, REQUEST_PAGINATED_SEARCH, RESET_STATE)
            )
          )
        ),
      ]);
    }),
    RxOp.concatAll()
  );

const translateRequestEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSLATE_REQUEST),
    RxOp.mergeMap(({ payload }) => {
      const state = state$.value;
      const timezoneMaybe = getUserTimezone(state);
      const timezone = S.isJust(timezoneMaybe) ? timezoneMaybe.value : undefined;
      // A natural-language submit is the other way a search is (re)submitted,
      // so it sweeps the alert stack the same way the search epic does.
      return Rx.concat(
        Rx.of(clearAllAlerts()),
        requestEpic({
          requestMethod: translateTransactionSearch,
          requestArgs: {
            token: S.fromMaybe("")(getAuthToken(state)),
            query: payload.query,
            agencyContext: getCurrentUserAgencyNames(state) || [],
            timezone,
          },
          onSuccess: (result) => Rx.of(translateSucceeded({ result: result.value })),
          onFailure: (error) =>
            Rx.of(translateFailed({ error: toTranslateError(error.value) })),
        })
      ).pipe(RxOp.takeUntil(action$.pipe(ofType(RESET_STATE))));
    })
  );

// The app-wide Overlay only dispatches the classic closeDatesModal, so relay
// it to close the beta calendar.
const closeDatesModalFromOverlayEpic = (action$, state$) =>
  action$.pipe(
    ofType(CLASSIC_CLOSE_DATES_MODAL),
    RxOp.filter(() => isDatesOpen(state$.value)),
    RxOp.map(() => closeDatesModal())
  );

export default combineEpics(
  updateSearchPendingTextEpic,
  performTransactionSearchEpic,
  translateRequestEpic,
  closeDatesModalFromOverlayEpic
);
